import { useState } from 'react';
import { color, severityTheme } from '../styles/theme';
import { dateTime, fullDate } from '../lib/format';
import { fieldErrorsFrom } from '../lib/fieldErrors';
import {
  useStaffRecords,
  useLogCall,
  useLogPrescreen,
  useRaiseFlag,
  useResolveFlag,
  useLogConsent,
  useReferralAck,
  useRecordOutcome,
  useMarkOutcomeSent,
  labelOf,
  CALL_OUTCOMES,
  ACUTE_OPTIONS,
  YES_NO_UNSURE,
  RECOMMENDATIONS,
  FLAG_CATEGORIES,
  FLAG_SEVERITIES,
  FLAG_NOTIFIED,
  FLAG_ACTIONS,
  CONSENT_DOCS,
  PRESCREEN_FIELDS,
  OUTCOME_CODES,
  OUTCOME_REASONS,
  OUTCOME_OFFERS,
  OUTCOME_LETTERS,
  type ReferralState,
  type ReferralCandidate,
} from '../api/staff';
import { ApiError } from '../api/client';
import {
  Button,
  Field,
  Eyebrow,
  SectionHead,
  ErrorNote,
  Loading,
  inputStyle,
  selectStyle,
} from '../components/ui';

/**
 * The staff forms from 02, inside the participant drawer.
 *
 * Everything here is staff-only. 02: "Never show internal eligibility scoring
 * to the public. Safety flags are alerts for clinicians, not public
 * auto-rejects." The helper text under each field is the pack's own, because it
 * is what stops a coordinator turning a coarse flag into a diagnosis.
 */

type Tab = 'log' | 'call' | 'prescreen' | 'flag' | 'consent' | 'outcome';

const today = () => new Date().toISOString().slice(0, 10);
const plusDays = (n: number) => new Date(Date.now() + n * 86_400_000).toISOString().slice(0, 10);

function Select({
  label,
  helper,
  value,
  onChange,
  options,
  error,
}: {
  label: string;
  helper?: string;
  value: string;
  onChange: (v: string) => void;
  options: ReadonlyArray<readonly [string, string]>;
  /** What the server said about this field, shown under its label. */
  error?: string;
}) {
  return (
    <Field label={label} hint={helper ? `— ${helper}` : undefined} error={error}>
      <select value={value} onChange={(e) => onChange(e.target.value)} style={selectStyle}>
        <option value="">Choose…</option>
        {options.map(([v, l]) => (
          <option key={v} value={v}>
            {l}
          </option>
        ))}
      </select>
    </Field>
  );
}

/** A payload key as a person reads it: `nextContactOn` → "Next contact on". */
function fieldName(key: string): string {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
}

/**
 * The summary above the form names every field the server refused.
 *
 * The message beside each field does most of the work, but a few keys the
 * server validates have no visible control — a study slug taken from context,
 * a timestamp set on submit — and a refusal on one of those would otherwise be
 * "Check the call details" and nothing else (WCAG 3.3.1).
 */
function summaryMessage(message: string, fields: Record<string, string>): string {
  const names = Object.keys(fields).map(fieldName);
  return names.length > 0 ? `${message} Check: ${names.join(', ')}.` : message;
}

export function StaffForms({
  submissionId,
  form,
  studySlug,
  canRead,
  canUpdate,
  hideFlagBanner = false,
}: {
  submissionId: string;
  form: string;
  studySlug: string | null;
  /**
   * `submissions:read_phi`. Every staff record is PHI and the API refuses the
   * read without it, so a role that lacks it is told so and nothing is asked.
   */
  canRead: boolean;
  canUpdate: boolean;
  /** Set when the surrounding record already raises open flags at the top. */
  hideFlagBanner?: boolean;
}) {
  const records = useStaffRecords(canRead ? submissionId : null);
  const [tab, setTab] = useState<Tab>('log');
  const [error, setError] = useState<string | null>(null);
  /** Per-field messages from the last rejected save, keyed as the API keys them. */
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const logCall = useLogCall();
  const logPrescreen = useLogPrescreen();
  const raiseFlag = useRaiseFlag();
  const resolve = useResolveFlag();
  const logConsent = useLogConsent();
  const ack = useReferralAck();
  const recordOutcome = useRecordOutcome();
  const markSent = useMarkOutcomeSent();

  async function run(fn: () => Promise<unknown>, onDone: () => void) {
    setError(null);
    setFieldErrors({});
    try {
      await fn();
      onDone();
      setTab('log');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not save that.');
      // WCAG 3.3.1: the server says which fields it refused and why; without
      // this the staff member reads "Check the worksheet" and has to guess.
      if (e instanceof ApiError) setFieldErrors(fieldErrorsFrom(e.details));
    }
  }

  if (!canRead) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <SectionHead>Staff forms — never shown to the participant</SectionHead>
        <p style={{ fontSize: 13, color: color.muted, margin: 0 }}>
          Your role cannot see staff records — call notes, pre-screens, safety flags, consents and decisions —
          because they hold health details.
        </p>
      </div>
    );
  }
  if (records.isLoading) return <Loading label="Loading staff records…" />;
  if (records.error) return <ErrorNote message={(records.error as Error).message} />;
  const r = records.data;
  if (!r) return null;

  const prescreenStudy = studySlug && PRESCREEN_FIELDS[studySlug] ? studySlug : null;

  const tabs: Array<[Tab, string]> = [
    ['log', `Log (${r.counts.calls + r.counts.prescreens + r.counts.flags + r.counts.consents + r.outcomes.length})`],
    ['call', 'Phone intake'],
    ...(prescreenStudy ? ([['prescreen', 'Pre-screen']] as Array<[Tab, string]>) : []),
    ['flag', 'Safety flag'],
    ['consent', 'Consent'],
    ['outcome', 'Decision'],
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <SectionHead>Staff forms — never shown to the participant</SectionHead>

      {!hideFlagBanner && r.counts.openFlags > 0 && (
        <p
          style={{
            background: '#fdf1ee',
            border: '1px solid #f3d5cc',
            color: color.red,
            borderRadius: 12,
            padding: '10px 14px',
            fontSize: 12.5,
            fontWeight: 700,
            margin: 0,
          }}
        >
          {r.counts.openFlags} open safety {r.counts.openFlags === 1 ? 'flag' : 'flags'} — this
          person cannot be marked enrolled until they are closed.
        </p>
      )}

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {tabs.map(([t, label]) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={tab === t ? undefined : 'hov-line'}
            style={{
              border: `1px solid ${tab === t ? color.navy : color.line}`,
              background: tab === t ? color.navy : color.white,
              color: tab === t ? color.white : color.ink,
              fontSize: 12,
              fontWeight: 700,
              padding: '6px 12px',
              borderRadius: 999,
              cursor: 'pointer',
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {error && <ErrorNote message={summaryMessage(error, fieldErrors)} />}
      {!canUpdate && tab !== 'log' && (
        <p style={{ fontSize: 12.5, color: color.muted, margin: 0 }}>
          Your role can read these but not add to them.
        </p>
      )}

      {tab === 'log' && (
        <RecordLog
          records={r}
          onResolve={(flagId) => resolve.mutate({ id: submissionId, flagId })}
          onSent={(outcomeId) => markSent.mutate({ id: submissionId, outcomeId })}
          canUpdate={canUpdate}
        />
      )}
      {tab === 'call' && canUpdate && (
        <CallForm
          errors={fieldErrors}
          busy={logCall.isPending}
          studySlug={studySlug ?? ''}
          onSubmit={(body) => void run(() => logCall.mutateAsync({ id: submissionId, body }), () => {})}
        />
      )}
      {tab === 'prescreen' && canUpdate && prescreenStudy && (
        <PrescreenForm
          errors={fieldErrors}
          study={prescreenStudy}
          busy={logPrescreen.isPending}
          onSubmit={(body) => void run(() => logPrescreen.mutateAsync({ id: submissionId, body }), () => {})}
        />
      )}
      {tab === 'flag' && canUpdate && (
        <FlagForm
          errors={fieldErrors}
          busy={raiseFlag.isPending}
          onSubmit={(body) => void run(() => raiseFlag.mutateAsync({ id: submissionId, body }), () => {})}
        />
      )}
      {tab === 'outcome' && canUpdate && (
        <OutcomeForm
          errors={fieldErrors}
          busy={recordOutcome.isPending}
          onSubmit={(body) => void run(() => recordOutcome.mutateAsync({ id: submissionId, body }), () => {})}
        />
      )}
      {tab === 'consent' && canUpdate && (
        <ConsentForm
          errors={fieldErrors}
          studySlug={studySlug ?? ''}
          busy={logConsent.isPending}
          onSubmit={(body) => void run(() => logConsent.mutateAsync({ id: submissionId, body }), () => {})}
        />
      )}

      {form === 'referral' && canUpdate && r.referral && (
        <ReferralAck
          state={r.referral}
          busy={ack.isPending}
          onSave={(body) => void run(() => ack.mutateAsync({ id: submissionId, body }), () => {})}
        />
      )}

      {form === 'referral-status' && <ReferralCandidates candidates={r.referralCandidates} />}
    </div>
  );
}

function RecordLog({
  records,
  onResolve,
  onSent,
  canUpdate,
}: {
  records: NonNullable<ReturnType<typeof useStaffRecords>['data']>;
  onResolve: (flagId: string) => void;
  onSent: (outcomeId: string) => void;
  canUpdate: boolean;
}) {
  const empty =
    records.calls.length + records.prescreens.length + records.flags.length +
      records.consents.length + records.outcomes.length === 0;
  if (empty) {
    return (
      <p style={{ fontSize: 13, color: color.muted, margin: 0 }}>
        Nothing logged yet.
      </p>
    );
  }

  const box = {
    background: color.bg,
    border: `1px solid ${color.line}`,
    borderRadius: 12,
    padding: '10px 12px',
  } as const;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {records.flags.map((f) => {
        const tone = severityTheme[f.severity as keyof typeof severityTheme] ?? severityTheme.watch;
        return (
          <div key={f.id} style={{ ...box, borderColor: f.resolvedAt ? color.line : '#f3d5cc' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span
                style={{
                  background: tone.bg,
                  color: tone.fg,
                  fontSize: 10.5,
                  fontWeight: 800,
                  padding: '3px 9px',
                  borderRadius: 999,
                }}
              >
                {labelOf(FLAG_SEVERITIES, f.severity)}
              </span>
              <span style={{ fontSize: 12.5, fontWeight: 700, color: color.ink }}>
                {labelOf(FLAG_CATEGORIES, f.category)}
              </span>
              <span style={{ flex: 1 }} />
              {f.resolvedAt ? (
                <span style={{ fontSize: 11, color: color.muted }}>Closed</span>
              ) : (
                canUpdate && (
                  <button
                    onClick={() => onResolve(f.id)}
                    className="hov-line"
                    style={{
                      border: `1px solid ${color.line}`,
                      background: color.white,
                      fontSize: 11.5,
                      fontWeight: 700,
                      padding: '4px 10px',
                      borderRadius: 999,
                      cursor: 'pointer',
                      color: color.ink,
                    }}
                  >
                    Close flag
                  </button>
                )
              )}
            </div>
            <p style={{ fontSize: 12.5, lineHeight: '19px', color: color.ink, margin: '6px 0 0' }}>
              {f.note}
            </p>
            <p style={{ fontSize: 11, color: color.muted, margin: '6px 0 0' }}>
              {labelOf(FLAG_ACTIONS, f.action)} · notified {labelOf(FLAG_NOTIFIED, f.notified)} ·{' '}
              {f.raisedByName} · {dateTime(f.createdAt)}
            </p>
          </div>
        );
      })}

      {records.calls.map((c) => (
        <div key={c.id} style={box}>
          <p style={{ fontSize: 12.5, fontWeight: 700, color: color.ink, margin: 0 }}>
            Phone intake — {labelOf(CALL_OUTCOMES, c.outcome)}
          </p>
          <p style={{ fontSize: 12.5, lineHeight: '19px', color: color.ink, margin: '6px 0 0' }}>
            {c.notes}
          </p>
          <p style={{ fontSize: 11, color: color.muted, margin: '6px 0 0' }}>
            Acute: {labelOf(ACUTE_OPTIONS, c.acute)} · travel {c.travelNh} · next contact{' '}
            {fullDate(c.nextContactOn)} · {c.staffName} · {dateTime(c.calledAt)}
          </p>
        </div>
      ))}

      {records.prescreens.map((p) => (
        <div key={p.id} style={box}>
          <p style={{ fontSize: 12.5, fontWeight: 700, color: color.ink, margin: 0 }}>
            Pre-screen ({p.studySlug}) — {labelOf(RECOMMENDATIONS, p.recommendation)}
          </p>
          <div style={{ display: 'grid', gap: 2, margin: '6px 0 0' }}>
            {Object.entries(p.answers).map(([k, v]) => (
              <span key={k} style={{ fontSize: 11.5, color: color.muted }}>
                {k} · <strong style={{ color: color.ink }}>{v}</strong>
              </span>
            ))}
          </div>
          <p style={{ fontSize: 12.5, lineHeight: '19px', color: color.ink, margin: '6px 0 0' }}>
            {p.notes}
          </p>
          <p style={{ fontSize: 11, color: color.muted, margin: '6px 0 0' }}>
            {p.reviewerName} · {dateTime(p.createdAt)}
          </p>
        </div>
      ))}

      {records.outcomes.map((o) => (
        <div key={o.id} style={{ ...box, borderColor: o.sentAt ? color.line : '#f3cfa5' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: color.ink }}>
              Decision — {labelOf(OUTCOME_CODES, o.code)}
            </span>
            <span style={{ flex: 1 }} />
            {o.sentAt ? (
              <span style={{ fontSize: 11, color: color.muted }}>Letter sent {fullDate(o.sentAt)}</span>
            ) : (
              canUpdate && (
                <button
                  onClick={() => onSent(o.id)}
                  className="hov-line"
                  style={{
                    border: `1px solid ${color.line}`,
                    background: color.white,
                    color: color.ink,
                    fontSize: 11.5,
                    fontWeight: 700,
                    padding: '4px 10px',
                    borderRadius: 999,
                    cursor: 'pointer',
                  }}
                >
                  Mark letter sent
                </button>
              )
            )}
          </div>
          <p style={{ fontSize: 12, color: color.ink, margin: '6px 0 0' }}>
            Internal reason: {labelOf(OUTCOME_REASONS, o.reasonInternal)} · letter:{' '}
            {labelOf(OUTCOME_LETTERS, o.letter)}
            {o.offer !== 'none' && <> · offered {labelOf(OUTCOME_OFFERS, o.offer)}</>}
          </p>
          <p style={{ fontSize: 11, color: color.muted, margin: '6px 0 0' }}>
            {o.decidedByName} · {dateTime(o.createdAt)}
            {!o.sentAt && ' · the letter is due within 2 business days'}
          </p>
        </div>
      ))}

      {records.consents.map((c) => (
        <div key={c.id} style={box}>
          <p style={{ fontSize: 12.5, fontWeight: 700, color: color.ink, margin: 0 }}>
            {labelOf(CONSENT_DOCS, c.document)} — {c.irbVersion}
          </p>
          <p style={{ fontSize: 11, color: color.muted, margin: '6px 0 0' }}>
            Signed {fullDate(c.signedOn)} · filed at {c.storage} · {c.staffName}
          </p>
        </div>
      ))}
    </div>
  );
}

function CallForm({
  busy,
  studySlug,
  errors,
  onSubmit,
}: {
  busy: boolean;
  studySlug: string;
  errors: Record<string, string>;
  onSubmit: (body: Record<string, unknown>) => void;
}) {
  const [acute, setAcute] = useState('');
  const [travelNh, setTravelNh] = useState('');
  const [transport, setTransport] = useState('');
  const [currentCare, setCurrentCare] = useState('');
  const [outcome, setOutcome] = useState('');
  const [notes, setNotes] = useState('');
  const [idConfirmed, setIdConfirmed] = useState(false);
  const [nextContactOn, setNextContactOn] = useState(plusDays(3));

  const ready = acute && travelNh && outcome && notes.trim().length >= 8;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <p
        style={{
          background: color.tint,
          borderRadius: 12,
          padding: '10px 12px',
          fontSize: 12,
          lineHeight: '18px',
          color: color.blueDeep,
          margin: 0,
        }}
      >
        Informational call, not a decision about the study and not a doctor's appointment. If they
        are in immediate danger, 988 — stop the research script.
      </p>

      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, fontWeight: 700, color: color.muted }}>
        <input
          type="checkbox"
          checked={idConfirmed}
          onChange={() => setIdConfirmed((v) => !v)}
          style={{ width: 14, height: 14, accentColor: color.blue }}
        />
        Identity confirmed
      </label>

      <Select label="Acute safety now" error={errors.acute} helper="If yes, stop the research script" value={acute} onChange={setAcute} options={ACUTE_OPTIONS} />
      <Select label="Can attend in-person New Haven visits" error={errors.travelNh} value={travelNh} onChange={setTravelNh} options={YES_NO_UNSURE} />
      <Select label="Support / transport after a long visit" error={errors.transport} value={transport} onChange={setTransport} options={YES_NO_UNSURE} />
      <Select label="Current care in place" error={errors.currentCare} helper="Do not advise stopping meds on this call" value={currentCare} onChange={setCurrentCare} options={YES_NO_UNSURE} />
      <Select label="Outcome" error={errors.outcome} helper="The participant-visible status maps from this" value={outcome} onChange={setOutcome} options={CALL_OUTCOMES} />

      <Field label="Call notes" error={errors.notes} hint="— minimum necessary. No novel diagnosis.">
        <textarea
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          style={{ ...inputStyle, resize: 'vertical' }}
        />
      </Field>

      <Field label="Next contact by" error={errors.nextContactOn}>
        <input type="date" value={nextContactOn} onChange={(e) => setNextContactOn(e.target.value)} style={inputStyle} />
      </Field>

      <Button
        disabled={!ready || busy}
        onClick={() =>
          onSubmit({
            calledAt: new Date().toISOString(),
            studySlug,
            idConfirmed,
            acute,
            travelNh,
            transport: transport || undefined,
            currentCare: currentCare || undefined,
            outcome,
            notes,
            nextContactOn,
          })
        }
        style={{ alignSelf: 'flex-start' }}
      >
        {busy ? 'Saving…' : 'Log call'}
      </Button>
    </div>
  );
}

function PrescreenForm({
  study,
  busy,
  errors,
  onSubmit,
}: {
  study: string;
  busy: boolean;
  errors: Record<string, string>;
  onSubmit: (body: Record<string, unknown>) => void;
}) {
  const fields = PRESCREEN_FIELDS[study] ?? [];
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [recommendation, setRecommendation] = useState('');
  const [notes, setNotes] = useState('');

  const ready =
    recommendation &&
    notes.trim().length >= 8 &&
    fields.filter((f) => f.required).every((f) => answers[f.key]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <p
        style={{
          background: color.tint,
          borderRadius: 12,
          padding: '10px 12px',
          fontSize: 12,
          lineHeight: '18px',
          color: color.blueDeep,
          margin: 0,
        }}
      >
        Structured flags for clinicians. Not an auto-reject engine, and nobody is enrolled from this
        sheet. The exact inclusion and exclusion checklist comes from the IRB protocol.
      </p>

      {fields.map((f) => (
        <Select
          key={f.key}
          label={`${f.label}${f.required ? ' *' : ''}`}
          helper={f.helper}
          error={errors[f.key]}
          value={answers[f.key] ?? ''}
          onChange={(v) => setAnswers((a) => ({ ...a, [f.key]: v }))}
          options={f.options}
        />
      ))}

      <Select label="Recommendation *" error={errors.recommendation} helper="Participant-visible language is separate" value={recommendation} onChange={setRecommendation} options={RECOMMENDATIONS} />

      <Field label="Notes" error={errors.notes} hint="— minimum necessary">
        <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} style={{ ...inputStyle, resize: 'vertical' }} />
      </Field>

      <Button
        disabled={!ready || busy}
        onClick={() =>
          onSubmit({
            studySlug: study,
            answers: Object.fromEntries(Object.entries(answers).filter(([, v]) => v)),
            recommendation,
            notes,
          })
        }
        style={{ alignSelf: 'flex-start' }}
      >
        {busy ? 'Saving…' : 'Record worksheet'}
      </Button>
    </div>
  );
}

function FlagForm({ busy, errors, onSubmit }: { busy: boolean; errors: Record<string, string>; onSubmit: (b: Record<string, unknown>) => void }) {
  const [category, setCategory] = useState('');
  const [severity, setSeverity] = useState('');
  const [note, setNote] = useState('');
  const [notified, setNotified] = useState('');
  const [action, setAction] = useState('');
  const ready = category && severity && notified && action && note.trim().length >= 8;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <p style={{ fontSize: 12, lineHeight: '18px', color: color.muted, margin: 0 }}>
        An alert for a clinician. Never an auto-reject, and the label is never shown to the
        participant. Their status does not change automatically.
      </p>
      <Select label="Category *" error={errors.category} helper="Staff taxonomy, not participant language" value={category} onChange={setCategory} options={FLAG_CATEGORIES} />
      <Select label="Severity *" error={errors.severity} value={severity} onChange={setSeverity} options={FLAG_SEVERITIES} />
      <Field label="Description *" error={errors.note} hint="— facts, not scores">
        <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} style={{ ...inputStyle, resize: 'vertical' }} />
      </Field>
      <Select label="Notified *" error={errors.notified} value={notified} onChange={setNotified} options={FLAG_NOTIFIED} />
      <Select label="Action *" error={errors.action} value={action} onChange={setAction} options={FLAG_ACTIONS} />
      <Button
        disabled={!ready || busy}
        onClick={() => onSubmit({ category, severity, note, notified, action })}
        style={{ alignSelf: 'flex-start' }}
      >
        {busy ? 'Saving…' : 'Raise flag'}
      </Button>
    </div>
  );
}

function ConsentForm({
  studySlug,
  busy,
  errors,
  onSubmit,
}: {
  studySlug: string;
  busy: boolean;
  errors: Record<string, string>;
  onSubmit: (b: Record<string, unknown>) => void;
}) {
  const [document, setDocument] = useState('');
  const [irbVersion, setIrbVersion] = useState('');
  const [signedOn, setSignedOn] = useState(today());
  const [storage, setStorage] = useState('');
  const ready = document && irbVersion.trim() && signedOn && storage.trim() && studySlug;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <p style={{ fontSize: 12, lineHeight: '18px', color: color.muted, margin: 0 }}>
        Version, date, staff. Not a substitute for the IRB-stamped form. A reconsent is a new row.
      </p>
      {!studySlug && <ErrorNote message="This submission has no study, so a consent cannot be filed against one." />}
      <Select label="Document *" error={errors.document} value={document} onChange={setDocument} options={CONSENT_DOCS} />
      <Field label="IRB version / date *" error={errors.irbVersion} hint="— stamp from the PDF">
        <input value={irbVersion} onChange={(e) => setIrbVersion(e.target.value)} placeholder="TD-ICF-v3.2 / 12 May 2026" style={inputStyle} />
      </Field>
      <Field label="Signed date *" error={errors.signedOn}>
        <input type="date" value={signedOn} onChange={(e) => setSignedOn(e.target.value)} style={inputStyle} />
      </Field>
      <Field label="Storage *" error={errors.storage} hint="— e.g. eReg binder row">
        <input value={storage} onChange={(e) => setStorage(e.target.value)} placeholder="eReg binder row 12" style={inputStyle} />
      </Field>
      <Button
        disabled={!ready || busy}
        onClick={() => onSubmit({ studySlug, document, irbVersion, signedOn, storage })}
        style={{ alignSelf: 'flex-start' }}
      >
        {busy ? 'Saving…' : 'Log consent'}
      </Button>
    </div>
  );
}

/**
 * 02.18. Every field is required and none is pre-filled, because each one is
 * a decision somebody has to make: which letter a person receives, and
 * whether another study is "genuinely plausible", should never be a default.
 */
function OutcomeForm({ busy, errors, onSubmit }: { busy: boolean; errors: Record<string, string>; onSubmit: (b: Record<string, unknown>) => void }) {
  const [code, setCode] = useState('');
  const [reasonInternal, setReason] = useState('');
  const [offer, setOffer] = useState('');
  const [letter, setLetter] = useState('');
  const ready = code && reasonInternal && offer && letter;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <p style={{ fontSize: 12, lineHeight: '18px', color: color.muted, margin: 0 }}>
        Sets the participant-visible status. The internal reason stays here — it is never mailed and
        never shown to the participant. Send the letter from the conversation panel within 2 business
        days, then mark it sent.
      </p>
      <Select label="Decision *" error={errors.code} value={code} onChange={setCode} options={OUTCOME_CODES} />
      <Select
        label="Internal reason (never mailed) *"
        error={errors.reasonInternal}
        helper="Staff only"
        value={reasonInternal}
        onChange={setReason}
        options={OUTCOME_REASONS}
      />
      <Select
        label="Offer another ENACT study? *"
        error={errors.offer}
        helper="Only if genuinely plausible"
        value={offer}
        onChange={setOffer}
        options={OUTCOME_OFFERS}
      />
      <Select label="Letter template *" error={errors.letter} value={letter} onChange={setLetter} options={OUTCOME_LETTERS} />
      <Button
        disabled={!ready || busy}
        onClick={() => onSubmit({ code, reasonInternal, offer, letter })}
        style={{ alignSelf: 'flex-start' }}
      >
        {busy ? 'Saving…' : 'Record decision'}
      </Button>
    </div>
  );
}

/**
 * 02.14, with the consequence stated before the click: acknowledging the
 * clinician is also what starts the referred patient's clocks.
 */
/**
 * 07 §3 — the referrals a status ping might be about, so answering one does
 * not start with a search. Candidates only: the clinician matches exactly, the
 * study they named sorts first, and the person reading decides which it is.
 * The pack's other half of the key, the date they sent it, is not here because
 * the form never asks for it — it asks only how they remember it.
 */
function ReferralCandidates({ candidates }: { candidates: ReferralCandidate[] }) {
  return (
    <div style={{ borderTop: `1px solid ${color.line}`, paddingTop: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <Eyebrow>Referrals this clinician has sent</Eyebrow>
      {candidates.length === 0 ? (
        <p style={{ fontSize: 12.5, color: color.muted, margin: 0 }}>
          None on record for this address. They may have written from a different one — search the
          referrals by name before answering.
        </p>
      ) : (
        candidates.map((c) => (
          <div
            key={c.submissionId}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
              padding: '8px 10px',
              borderRadius: 8,
              background: c.matchesStudy ? color.tint : 'transparent',
              border: `1px solid ${color.line}`,
            }}
          >
            <p style={{ fontSize: 13, fontWeight: 600, color: color.ink, margin: 0 }}>
              {c.patientName ?? 'No patient contact recorded'}
              {c.matchesStudy && (
                <span style={{ fontSize: 11.5, fontWeight: 600, color: color.muted }}> · the study they named</span>
              )}
            </p>
            <p style={{ fontSize: 12, color: color.muted, margin: 0 }}>
              Sent {fullDate(c.sentAt)} · {c.studySlug ?? 'no study named'} · {c.status}
              {c.ackAt ? ` · acknowledged ${fullDate(c.ackAt)}` : ' · not yet acknowledged'}
              {c.outreachAt ? ` · patient reached ${fullDate(c.outreachAt)}` : ''}
            </p>
            <p style={{ fontSize: 11, color: color.muted, margin: 0, fontFamily: 'ui-monospace, monospace' }}>
              {c.submissionId}
            </p>
          </div>
        ))
      )}
      <p style={{ fontSize: 11.5, color: color.muted, margin: 0 }}>
        What may be said back is still the coordinator's call: a clinician hears about their own
        referral only, and clinical detail only where the patient authorized it.
      </p>
    </div>
  );
}

function ReferralAck({
  state,
  busy,
  onSave,
}: {
  state: ReferralState;
  busy: boolean;
  onSave: (body: Record<string, unknown>) => void;
}) {
  const [updateOk, setUpdateOk] = useState(state.clinicianUpdateOk ? 'yes' : 'no');
  const clinicianUpdateOk = updateOk === 'yes';
  const whatHappens = !state.hasPatient
    ? 'No usable contact was given for the patient, so no patient outreach will start.'
    : state.patientSuppressed
      ? 'The patient has asked us to stop contacting them, so no patient outreach will start.'
      : "This also starts the patient's first-contact and call-offer clocks.";

  return (
    <div style={{ borderTop: `1px solid ${color.line}`, paddingTop: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <Eyebrow>Referral acknowledgment — SLA 1 business day</Eyebrow>
      <Select
        label="Clinician update authorized?"
        helper="Default No"
        value={updateOk}
        onChange={setUpdateOk}
        options={[['yes', 'Yes, on file'], ['no', 'No']]}
      />
      {state.ackAt ? (
        <>
          <p style={{ fontSize: 12.5, color: color.ink, margin: 0 }}>
            Clinician acknowledged {dateTime(state.ackAt)}.
            {state.outreachAt ? ` Patient reached ${dateTime(state.outreachAt)}.` : ''}
          </p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {!state.outreachAt && state.hasPatient && !state.patientSuppressed && (
              <Button
                variant="ghost"
                disabled={busy}
                onClick={() => onSave({ clinicianUpdateOk, outreachAt: new Date().toISOString() })}
              >
                {busy ? 'Recording…' : 'Mark patient reached'}
              </Button>
            )}
            {updateOk !== (state.clinicianUpdateOk ? 'yes' : 'no') && (
              <Button
                variant="ghost"
                disabled={busy}
                onClick={() => onSave({ clinicianUpdateOk, outreachAt: state.outreachAt })}
              >
                Save authorization
              </Button>
            )}
          </div>
        </>
      ) : (
        <>
          <Button
            variant="ghost"
            disabled={busy}
            onClick={() => onSave({ ackAt: new Date().toISOString(), clinicianUpdateOk })}
            style={{ alignSelf: 'flex-start' }}
          >
            {busy ? 'Recording…' : 'Mark clinician acknowledged'}
          </Button>
          <p style={{ fontSize: 11.5, color: color.muted, margin: 0 }}>{whatHappens}</p>
        </>
      )}
      <p style={{ fontSize: 11.5, color: color.muted, margin: 0 }}>
        Clinical detail is never sent back to the clinician without that authorization on file.
      </p>
    </div>
  );
}
